import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";

function EditScheduleModal({ schedule, onClose, onSaved }) {
  const [form, setForm] = useState({
    topic: schedule.topic || "",
    start_time: schedule.start_time?.slice(0, 16) || "",
    link: schedule.link || "",
    recording_link: schedule.recording_link || "",
    type: schedule.type || "lecture",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("_method", "PATCH");
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await client.post(`/schedules/${schedule.id}`, fd);
      toast.success("Class updated.");
      onSaved();
    } catch { toast.error("Failed to update."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-5">Edit Class</h2>
        <div className="space-y-3">
          <input placeholder="Topic" value={form.topic}
            onChange={e => setForm(f => ({...f, topic: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <input type="datetime-local" value={form.start_time}
            onChange={e => setForm(f => ({...f, start_time: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <input placeholder="Class link" value={form.link}
            onChange={e => setForm(f => ({...f, link: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <input placeholder="Recording link" value={form.recording_link}
            onChange={e => setForm(f => ({...f, recording_link: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30">
            <option value="lecture">Lecture</option>
            <option value="workshop">Workshop</option>
            <option value="qa_session">Q&A Session</option>
            <option value="makeup">Makeup Class</option>
            <option value="other">Other</option>
          </select>
          {form.type === "makeup" && (
            <p className="text-xs text-gray-400">Shows on the student dashboard like a normal class, but attendance isn't tracked.</p>
          )}
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#1e3a7a] transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentsPanel({ schedule, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const bottomRef = useRef(null);

  const load = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/admin/comments", { params: { schedule_id: schedule.id } })
      .then(res => setComments(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await client.post(`/schedules/${schedule.id}/comments`, { comment: text });
      setText("");
      load();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch { toast.error("Failed to post comment."); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    try {
      await client.delete(`/comments/${id}`);
      load();
    } catch { toast.error("Failed to delete."); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">Comments</h2>
            <p className="text-xs text-gray-400 truncate max-w-[280px]">{schedule.topic}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : comments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No comments yet</p>
          ) : comments.map(c => (
            <div key={c.id} className={`flex gap-2 ${c.parent_id ? "ml-6" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-[#294a93]/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#294a93]">
                {c.firstname?.[0]}{c.lastname?.[0]}
              </div>
              <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2">
                <p className="text-xs font-semibold text-gray-700">{c.firstname} {c.lastname}</p>
                <p className="text-sm text-gray-700 mt-0.5">{c.comment}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                  <button onClick={() => del(c.id)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-2">
          <input value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && post()}
            placeholder="Write a comment…"
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <button onClick={post} disabled={posting || !text.trim()}
            className="px-4 py-2.5 bg-[#294a93] text-white text-sm font-semibold rounded-xl disabled:opacity-50 hover:bg-[#1e3a7a] transition-colors">
            {posting ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AttendancePanel({ schedule, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get(`/admin/schedules/${schedule.id}/attendance`)
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load attendance."))
      .finally(() => setLoading(false));
  }, [schedule.id]);

  const methodColor = (method) => {
    if (method === "self_report") return "bg-green-100 text-green-700";
    if (method === "manual") return "bg-amber-100 text-amber-700";
    if (method === "zoom_webhook") return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-500";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">Attendance</h2>
            <p className="text-xs text-gray-400 truncate max-w-[280px]">{schedule.topic}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading ? (
            <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Attended ({data?.attended?.length ?? 0})
                </p>
                {(!data?.attended || data.attended.length === 0) ? (
                  <p className="text-sm text-gray-400">No one has marked attendance yet.</p>
                ) : (
                  <div className="space-y-2">
                    {data.attended.map(a => (
                      <div key={a.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-xl px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-700 truncate">{a.user?.firstname} {a.user?.lastname}</p>
                          <p className="text-xs text-gray-400 truncate">{a.user?.email}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${methodColor(a.method)}`}>{a.method}</span>
                          <p className="text-xs text-gray-400 mt-1">{new Date(a.attended_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Not Yet Attended ({data?.not_attended?.length ?? 0})
                </p>
                {(!data?.not_attended || data.not_attended.length === 0) ? (
                  <p className="text-sm text-gray-400">Everyone enrolled has attended.</p>
                ) : (
                  <div className="space-y-2">
                    {data.not_attended.map(u => (
                      <div key={u.id} className="bg-gray-50 rounded-xl px-3 py-2">
                        <p className="text-sm font-semibold text-gray-700">{u.firstname} {u.lastname}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StudentsSection({ courseId, cohortId }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/admin/subscriptions", { params: { course_id: courseId, cohort_id: cohortId } })
      .then(res => setStudents(res.data.data || []))
      .catch(() => toast.error("Failed to load students."))
      .finally(() => setLoading(false));
  }, [courseId, cohortId]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div>
          <p className="font-semibold text-gray-800 text-sm">Students</p>
          <p className="text-xs text-gray-400 mt-0.5">{loading ? "Loading…" : `${students.length} registered`}</p>
        </div>
        <span className="text-gray-400 text-sm">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        loading ? (
          <div className="px-5 pb-4 space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : students.length === 0 ? (
          <div className="px-5 pb-4 text-sm text-gray-400">No students registered for this course in this cohort.</div>
        ) : (
          <div className="overflow-x-auto border-t border-gray-100">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{["Name", "Email", "Status", "Registered"].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {students.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{s.user?.name || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.user?.email || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700 capitalize">{s.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {s.payment_date ? new Date(s.payment_date).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default function AdminCohortSchedulesPage() {
  const { courseId, cohortId } = useParams();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohortName, setCohortName] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [commentModal, setCommentModal] = useState(null);
  const [attendanceModal, setAttendanceModal] = useState(null);

  const load = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    client.get(`/admin/cohorts/${cohortId}/schedules`, { params: { course_id: courseId } })
      .then(res => {
        setSchedules(res.data);
        if (res.data[0]?.cohort?.name) setCohortName(res.data[0].cohort.name);
      })
      .catch(() => toast.error("Failed to load schedules."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Also fetch cohort name from the course cohorts endpoint
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get(`/admin/courses/${courseId}/cohorts`)
      .then(res => {
        const found = res.data.find(c => String(c.id) === String(cohortId));
        if (found) setCohortName(found.name);
      });
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statusColor = (status) => {
    if (status === "live") return "bg-red-100 text-red-700";
    if (status === "completed") return "bg-gray-100 text-gray-500";
    return "bg-blue-100 text-blue-700";
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <button onClick={() => navigate(`/dashboard/admin/cohorts/${cohortId}`)}
        className="text-sm text-gray-400 hover:text-gray-700 transition-colors mb-1 block">
        ← Back
      </button>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">{cohortName || "Cohort Schedules"}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{schedules.length} class{schedules.length !== 1 ? "es" : ""}</p>
      </div>

      <StudentsSection courseId={courseId} cohortId={cohortId} />

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-white rounded-2xl border border-gray-100 animate-pulse" />)}</div>
      ) : schedules.length === 0 ? (
        <div className="p-16 text-center bg-white rounded-2xl border border-gray-100">
          <p className="text-4xl mb-3">📅</p>
          <p className="text-sm font-medium text-gray-600">No classes scheduled for this cohort</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[750px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Topic", "Course", "Date & Time", "Status", "Actions"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {schedules.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 max-w-[200px] truncate">
                    {s.topic || "—"}
                    {s.type === "makeup" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-violet-100 text-violet-700 align-middle">Makeup</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px] truncate">{s.course?.title || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {s.start_time ? new Date(s.start_time).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColor(s.status)}`}>
                      {s.status || "upcoming"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      {s.link && (
                        <a href={s.link} target="_blank" rel="noreferrer"
                          className="text-xs px-3 py-1.5 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors">
                          Join
                        </a>
                      )}
                      <button onClick={() => setEditModal(s)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-gray-600 transition-colors">
                        Edit
                      </button>
                      <button onClick={() => setCommentModal(s)}
                        className="relative text-xs px-3 py-1.5 rounded-lg border border-[#294a93]/30 hover:bg-[#294a93]/5 font-medium text-[#294a93] transition-colors">
                        Comments
                        {s.comments_count > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center leading-none">
                            {s.comments_count > 99 ? "99+" : s.comments_count}
                          </span>
                        )}
                      </button>
                      <button onClick={() => setAttendanceModal(s)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-gray-600 transition-colors">
                        Attendance
                      </button>
                      {s.recording_link && (
                        <a href={s.recording_link} target="_blank" rel="noreferrer"
                          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-gray-500 transition-colors">
                          Recording
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editModal && (
        <EditScheduleModal
          schedule={editModal}
          onClose={() => setEditModal(null)}
          onSaved={() => { setEditModal(null); load(); }}
        />
      )}
      {commentModal && (
        <CommentsPanel
          schedule={commentModal}
          onClose={() => setCommentModal(null)}
        />
      )}
      {attendanceModal && (
        <AttendancePanel
          schedule={attendanceModal}
          onClose={() => setAttendanceModal(null)}
        />
      )}
    </div>
  );
}
